import { useTranslation } from "@/language"
/**
 * AdminEditBookingModal
 * Two-panel modal for rescheduling a coaching session booking (Admin view)
 * Left: Session info + participant card
 * Right: Calendar + time slots + message + save button
 */

import { LinkIcon, XIcon } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { toast } from "sonner"
import { CalendarIcon, ClockIcon, MoneyIcon, UserIcon } from "@/components/icons"
import { Avatar } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Dialog, DialogClose, DialogContent } from "@/components/ui/dialog"
import { formatTime, toDateString } from "@/features/booking/utils/format"
import { getConnectedOAuthAccounts } from "@/features/settings/services/settings.service"
import { cn } from "@/lib"
import { useCoachingAvailability, useRescheduleCoachingBooking } from "../hooks/useCoachingBooking"
import { AdminCancelSessionModal } from "./AdminCancelSessionModal"

export interface BookingDetails {
  id: string
  date: string
  startTime: string
  endTime: string
  user: {
    id: string
    name: string | null
    username: string | null
    profilePictureUrl: string | null
    email: string | null
  }
  message?: string | null
}

export interface AdminEditBookingModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  booking: BookingDetails | null
  session: {
    id: string
    title: string
    coverImage?: string | null
    duration: number
    participationFee: "Free" | "Paid"
    price: number | null
    expert?: {
      id: string
      name: string | null
      username: string | null
      profilePictureUrl?: string | null
    }
  }
  onCancelSession?: (bookingId: string, message?: string) => void
  isCancelling?: boolean
}

function formatTimeDisplay(startTime: string, endTime: string): string {
  return `${formatTime(startTime)} - ${formatTime(endTime)}`
}

function formatDateForDisplay(dateStr: string): string {
  // Parse date string as local time (YYYY-MM-DD format)
  const [year, month, day] = dateStr.split("-").map(Number)
  const d = new Date(year, month - 1, day)
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

// Helper to get date string for comparison (YYYY-MM-DD) - using local time to match calendar display
function getDateString(date: Date): string {
  return toDateString(date)
}

// Helper to check if a date is in the past (using local time to match calendar display)
function isPastDate(date: Date): boolean {
  const today = new Date()
  const todayStr = toDateString(today)
  const dateStr = toDateString(date)
  return dateStr < todayStr
}

export function AdminEditBookingModal({
  open,
  onOpenChange,
  booking,
  session,
  onCancelSession,
  isCancelling = false,
}: AdminEditBookingModalProps) {
  const { t } = useTranslation("coaching")
  const [selectedDate, setSelectedDate] = useState<Date | undefined>()
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null)
  const [message, setMessage] = useState<string>("")
  const [hasGoogleConnected, setHasGoogleConnected] = useState<boolean | null>(null)
  const [showCancelModal, setShowCancelModal] = useState(false)

  const reschedule = useRescheduleCoachingBooking()

  // Check if user has Google Calendar connected
  useEffect(() => {
    if (open) {
      getConnectedOAuthAccounts()
        .then(({ accounts }) => {
          const hasGoogle = accounts.some(acc => acc.provider === "google" && acc.connected)
          setHasGoogleConnected(hasGoogle)
        })
        .catch(() => {
          setHasGoogleConnected(false)
        })
    }
  }, [open])

  // Reset state when modal opens/closes or booking changes
  useEffect(() => {
    if (open && booking) {
      // Pre-select the booked date - parse date string as local time
      const [year, month, day] = booking.date.split("-").map(Number)
      const bookedDate = new Date(year, month - 1, day)
      setSelectedDate(bookedDate)
      setSelectedSlotId(null)
      setMessage("") // Always start with empty message - admin types their own
    }
  }, [open, booking])

  // Get month/year from selected date for availability fetch
  const currentMonth = useMemo(() => {
    if (!selectedDate) {
      const now = new Date()
      return { month: now.getMonth() + 1, year: now.getFullYear() }
    }
    return { month: selectedDate.getMonth() + 1, year: selectedDate.getFullYear() }
  }, [selectedDate])

  // Fetch availability for the session
  const { data, isLoading: isLoadingAvailability } = useCoachingAvailability(
    session.id,
    currentMonth.month,
    currentMonth.year,
  )

  const slots = data?.slots ?? []

  // Get slots for selected date
  const selectedDateStr = selectedDate ? toDateString(selectedDate) : null
  const slotsForDate = selectedDateStr ? slots.filter(s => s.date === selectedDateStr) : []

  // Get the currently selected slot
  const selectedSlot = selectedSlotId ? slotsForDate.find(s => s.id === selectedSlotId) : null

  // Dates that have availability
  const datesWithSlots = useMemo(() => new Set(slots.map(s => s.date)), [slots])

  function handleDateSelect(date: Date | undefined) {
    setSelectedDate(date)
    setSelectedSlotId(null)
  }

  function handleSave() {
    if (!booking || !selectedSlotId) return

    reschedule.mutate(
      { bookingId: booking.id, newAvailabilityId: selectedSlotId, message },
      {
        onSuccess: () => {
          toast.success("Session rescheduled successfully")
          onOpenChange(false)
        },
        onError: (error: Error) => {
          toast.error(error.message || "Failed to reschedule session")
        },
      },
    )
  }

  function handleCancelSession() {
    setShowCancelModal(true)
  }

  const isSameTime =
    booking &&
    selectedSlot &&
    selectedSlot.startTime === booking.startTime &&
    selectedSlot.date === booking.date

  if (!booking) return null

  // Expert info
  const expertName = session.expert?.name || session.expert?.username || "Expert"
  const expertAvatarFallback = expertName[0]?.toUpperCase() || "E"

  // Booker info
  const bookerName = booking.user.name || booking.user.username || "User"
  const bookerAvatarFallback = bookerName[0]?.toUpperCase() || "U"

  // Combined display
  const displayName = `${expertName} & ${bookerName}`

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="!max-w-[1080px] max-h-[90vh] overflow-y-auto border-connect-glass-border bg-[#1B1B1B] p-0 text-connect-cream sm:rounded-[30px]"
        overlayClass="bg-[rgba(2,2,2,0.9)] backdrop-blur-[25px]"
        showCloseButton={false}
      >
        <div className="flex flex-col lg:flex-row">
          {/* Left Panel - Session Info */}
          <div className="flex flex-col gap-5 p-5 lg:w-[535px]">
            {/* Session Cover Image - First */}
            {session.coverImage && (
              <div className="relative h-[320px] w-full overflow-hidden rounded-[20px]">
                <img
                  src={session.coverImage}
                  alt={session.title}
                  className="h-full w-full object-cover"
                />
              </div>
            )}

            {/* Session Title - Second */}
            <h2 className="text-[24px] font-semibold leading-normal text-[#F2F0EB]/90">
              {session.title}
            </h2>

            {/* User Avatars - Figma design: two overlapping circular avatars with combined names */}
            <div className="flex items-center gap-3">
              {/* Expert Avatar - 40x40, stroke ring */}
              <div className="relative">
                <Avatar
                  src={session.expert?.profilePictureUrl ?? undefined}
                  fallback={expertAvatarFallback}
                  size="sm"
                  className="h-[40px] w-[40px] ring-2 ring-[#1B1B1B] ring-offset-0"
                />
              </div>
              {/* Booker Avatar - 40x40, overlapping, stroke ring */}
              <div className="relative -ml-7.5">
                <Avatar
                  src={booking.user.profilePictureUrl ?? undefined}
                  fallback={bookerAvatarFallback}
                  size="sm"
                  className="h-[40px] w-[40px] ring-2 ring-[#1B1B1B] ring-offset-0"
                />
              </div>
              {/* Combined names text */}
              <span className="font-montserrat text-[14px] font-semibold text-[#F2F0EB]">
                {displayName}
              </span>
            </div>

            {/* Session Meta - Fourth */}
            <div className="flex flex-col gap-2.5">
              {/* Date row */}
              <div className="flex items-center gap-2 text-[13px] text-[#F2F0EB]">
                <CalendarIcon size={18} className="shrink-0" />
                <span>{formatDateForDisplay(booking.date)}</span>
              </div>
              {/* Time row */}
              <div className="flex items-center gap-2 text-[13px] text-[#F2F0EB]">
                <ClockIcon className="shrink-0" size={18} />
                <span>
                  {Math.floor(session.duration / 60)} hr –{" "}
                  {formatTimeDisplay(booking.startTime, booking.endTime)}
                </span>
              </div>
              <div className="flex items-center gap-2 text-[13px] text-[#F2F0EB]/70">
                <UserIcon color="#F2F0EB" size={18} className="shrink-0" />
                <span>Private Session</span>
              </div>
              <div className="flex items-center gap-2">
                <MoneyIcon
                  size={18}
                  className="shrink-0"
                  color={session.participationFee === "Free" ? "#5a5b3f" : "#C86757"}
                />
                <span
                  className={cn(
                    "text-[13px] font-semibold",
                    session.participationFee === "Free"
                      ? "text-brand-olive"
                      : "text-brand-terracotta",
                  )}
                >
                  {session.participationFee === "Free"
                    ? "Free"
                    : session.price
                      ? `$${Math.round(session.price)}`
                      : "Paid"}
                </span>
              </div>
            </div>
          </div>

          {/* Right Panel - Edit Controls */}
          <div className="relative flex flex-1 flex-col gap-5 bg-[#252525] p-5 lg:w-[545px]">
            {/* Close Button - Pill shaped */}
            <DialogClose className="absolute right-4 top-4 z-10 flex size-[44px] cursor-pointer items-center justify-center rounded-full border border-[#F2F0EB26] bg-[#F2F0EB0D] text-[#F2F0EB] transition-opacity hover:opacity-80">
              <XIcon className="size-5 text-connect-cream" />
              <span className="sr-only">Close</span>
            </DialogClose>

            {/* Header */}
            <div className="flex flex-col gap-1 pt-4">
              <h3 className="text-[18px] font-semibold text-[#F2F0EB]/90">Edit Booking Details</h3>
              <p className="text-[14px] text-[#F2F0EB]/50">
                Manage an existing participant booking.
              </p>
            </div>

            {/* Calendar - Above Times Section */}
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={handleDateSelect}
              month={selectedDate || new Date()}
              onMonthChange={date => {
                if (date) {
                  setSelectedDate(date)
                }
              }}
              disabled={date => isPastDate(date) || !datesWithSlots.has(getDateString(date))}
              className="w-full rounded-[15px] bg-[#020202] p-[15px]"
              classNames={{
                month_caption: "flex items-center h-[24px]",
                caption_label: "text-[16px] font-semibold leading-normal text-connect-cream",
                nav: "absolute right-0 top-0 flex items-center gap-[5px]",
                button_previous: "size-[24px] p-0",
                button_next: "size-[24px] p-0",
                weekdays: "flex w-full text-xs justify-between",
                weekday:
                  "size-[44px] h-[18px] flex items-center justify-center text-[12px] font-semibold uppercase text-connect-cream/70",
                week: "mt-[5px] flex w-full justify-between",
                day: "size-[44px] text-[20px] leading-6.25 tracking-[-0.45px] p-0 font-normal aspect-square",
              }}
            />

            {/* Times Section */}
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <h4 className="text-[18px] font-semibold text-[#F2F0EB]/90">Available Times</h4>
                {selectedSlot && selectedDate ? (
                  <p className="text-[14px] font-semibold text-[#F2F0EB]/70">
                    {formatDateForDisplay(toDateString(selectedDate))} at{" "}
                    {formatTimeDisplay(selectedSlot.startTime, selectedSlot.endTime)}
                  </p>
                ) : booking ? (
                  <p className="text-[14px] font-semibold text-[#F2F0EB]/70">
                    {formatDateForDisplay(booking.date)} at{" "}
                    {formatTimeDisplay(booking.startTime, booking.endTime)}
                  </p>
                ) : null}
              </div>

              {/* Time Slots */}
              {isLoadingAvailability ? (
                <p className="text-[13px] text-[#F2F0EB]/50">Loading availability...</p>
              ) : slotsForDate.length > 0 ? (
                <div className="grid grid-cols-3 gap-2">
                  {slotsForDate.map(slot => {
                    const isSelected = slot.id === selectedSlotId
                    const isUnavailable = slot.isBooked

                    return (
                      <button
                        key={slot.id}
                        type="button"
                        disabled={isUnavailable}
                        onClick={() => !isUnavailable && setSelectedSlotId(slot.id)}
                        className={cn(
                          "flex h-[35px] items-center justify-center rounded-[10px] px-[11px] py-[6px] text-[13px] font-medium transition-colors",
                          isSelected && "bg-brand-terracotta text-[#0F0F0F]",
                          !isSelected &&
                            !isUnavailable &&
                            "border border-[#F2F0EB] text-[#F2F0EB] hover:bg-[#F2F0EB]/10",
                          isUnavailable &&
                            "cursor-not-allowed bg-[#F2F0EB]/5 text-[#F2F0EB]/30 line-through",
                        )}
                      >
                        {formatTime(slot.startTime)}-{formatTime(slot.endTime)}
                      </button>
                    )
                  })}
                </div>
              ) : selectedDate ? (
                <p className="text-[13px] text-[#F2F0EB]/50">
                  No time slots available for this date.
                </p>
              ) : (
                <p className="text-[13px] text-[#F2F0EB]/50">
                  Select a date to see available times.
                </p>
              )}
            </div>

            {/* Message Section - Editable textarea matching Figma design */}
            <div className="flex flex-col gap-2.5 rounded-[15px] border border-white/10 bg-[#1B1B1B] p-4">
              <p className="font-montserrat text-[14px] font-normal text-[#F2F0EB]/50">
                Message to {bookerName}
              </p>
              <textarea
                id="message"
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder={t("messageToParticipant")}
                className="min-h-[60px] w-full resize-none bg-transparent font-montserrat text-[14px] text-[#F2F0EB]/90 placeholder:text-[#F2F0EB]/30 focus:outline-none"
              />
            </div>

            {/* Spacer */}
            <div className="flex-1" />

            {/* Google Calendar Alert */}
            {hasGoogleConnected === false && (
              <div className="flex items-start gap-3 rounded-[15px] border border-brand-terracotta/30 bg-brand-terracotta/10 p-4">
                <LinkIcon className="mt-0.5 size-5 shrink-0 text-brand-terracotta" />
                <div className="flex flex-col gap-1">
                  <p className="font-montserrat text-[14px] font-medium text-brand-terracotta">
                    Google Calendar Required
                  </p>
                  <p className="font-montserrat text-[12px] text-[#F2F0EB]/70">
                    You must connect your Google Calendar to reschedule sessions.{" "}
                    <a
                      href="/settings/connected-accounts"
                      className="underline underline-offset-2 hover:text-brand-terracotta"
                    >
                      Connect Google
                    </a>
                  </p>
                </div>
              </div>
            )}

            {/* Buttons */}
            <div className="flex flex-col gap-3">
              <Button
                onClick={handleSave}
                disabled={
                  !selectedSlotId ||
                  isSameTime ||
                  reschedule.isPending ||
                  hasGoogleConnected === false
                }
                className={cn(
                  "h-[50px] w-full rounded-full text-[14px] font-semibold uppercase transition-colors",
                  "bg-[#F2F0EB] text-[#0F0F0F]",
                  "hover:bg-[#F2F0EB]/90",
                  "disabled:opacity-50",
                )}
              >
                {reschedule.isPending ? "Saving..." : "Save Changes"}
              </Button>

              <Button
                variant="outline"
                onClick={handleCancelSession}
                className="h-[50px] w-full rounded-full border border-brand-terracotta hover:border-brand-terracotta bg-transparent hover:bg-transparent text-[14px] font-semibold uppercase !text-brand-terracotta hover:!text-brand-terracotta"
              >
                Cancel Session
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>

      {/* Cancel Session Confirmation Modal */}
      <AdminCancelSessionModal
        open={showCancelModal}
        onOpenChange={setShowCancelModal}
        booking={booking}
        session={session}
        onCancel={onCancelSession ?? (() => {})}
        isCancelling={isCancelling}
      />
    </Dialog>
  )
}
